// Story 9.6 (ADO-1080), AC8 — the order-history screen's URL state.
//
// The URL is the single source of truth for the page, the page size, the sort and the
// four filters, so copying it, reloading it or reaching it with browser back/forward
// restores the same view with nothing re-entered. That is why this is a pure module:
// the screen reads its whole request out of `URLSearchParams` on every render and never
// keeps a second copy in component state that could disagree with the address bar.
//
// ── Parsing is total ──
// Nothing here throws or rejects. A URL can be typed, truncated or pasted from an older
// build, and the answer to a nonsense `page=-4` is the sensible view, not an error
// screen: a filter panel that refuses to render because of a bad query parameter is
// worse than one that ignores it. What each field falls back to is noted at its own
// reader. The ONE thing that is not silently repaired is `from > to` — AC9 says that
// window must reach the server and come back as the no-results state, so it is carried
// through untouched.
//
// ── Serialising is canonical ──
// A default is never written. Page 1, the default page size, newest first, an empty
// reference and an off toggle all serialise to NOTHING, so the unfiltered first page has the bare URL it
// had before this story, and two ways of arriving at the same view produce the same
// address (which is what makes browser history legible).

import { isIsoDay } from '../../design-system';
import {
  DEFAULT_ORDER_HISTORY_SORT,
  ORDER_HISTORY_SORTS,
  type OrderHistorySort,
} from '../../api/orders.client';
import { NO_ORDER_FILTERS, type OrderFilterValues } from './OrderFilters';

/** Query-parameter names. Deliberately the SAME names the API route accepts, so there
 *  is one vocabulary from the address bar to the Zod schema and no mapping table to
 *  drift. */
export const ORDER_HISTORY_PARAMS = {
  page: 'page',
  pageSize: 'pageSize',
  sort: 'sort',
  submittedFrom: 'submittedFrom',
  submittedTo: 'submittedTo',
  orderRef: 'orderRef',
  salesRep: 'salesRep',
} as const;

/** The whole request the screen makes, as the URL holds it. */
export interface OrderHistoryUrlState {
  readonly page: number;
  readonly pageSize: number;
  readonly sort: OrderHistorySort;
  readonly filters: OrderFilterValues;
}

/**
 * Read the screen's state out of the query string.
 *
 * `pageSizeOptions` is the allow-list the route enforces: a size outside it is a 400,
 * so a URL carrying one falls back to the default rather than being sent and failing.
 * That is also the security-relevant half — a free numeric size in the URL would let a
 * single request read an account's entire order history.
 */
export function parseOrderHistoryUrlState(
  params: URLSearchParams,
  pageSizeOptions: readonly number[],
  defaultPageSize: number,
): OrderHistoryUrlState {
  return {
    page: parsePage(params.get(ORDER_HISTORY_PARAMS.page)),
    pageSize: parsePageSize(
      params.get(ORDER_HISTORY_PARAMS.pageSize),
      pageSizeOptions,
      defaultPageSize,
    ),
    sort: parseSort(params.get(ORDER_HISTORY_PARAMS.sort)),
    filters: {
      submittedFrom: parseDay(params.get(ORDER_HISTORY_PARAMS.submittedFrom)),
      submittedTo: parseDay(params.get(ORDER_HISTORY_PARAMS.submittedTo)),
      orderRef: params.get(ORDER_HISTORY_PARAMS.orderRef)?.trim() ?? '',
      // Only the exact string 'true' is on. Anything else — including the literal
      // 'false' the toggle's own off state could produce — is off, so no URL can turn
      // the switch on by accident.
      salesRepOnly: params.get(ORDER_HISTORY_PARAMS.salesRep) === 'true',
    },
  };
}

/** A whole number ≥ 1, else page 1. An over-range page is NOT repaired here: the server
 *  clamps it to the last real page, which is what keeps a bookmarked deep link working
 *  as orders age out. */
function parsePage(raw: string | null): number {
  if (raw === null) return 1;
  const page = Number(raw);
  return Number.isInteger(page) && page >= 1 ? page : 1;
}

/** One of the allow-listed sizes, else the default. */
function parsePageSize(raw: string | null, options: readonly number[], fallback: number): number {
  if (raw === null) return fallback;
  const size = Number(raw);
  return options.includes(size) ? size : fallback;
}

/** One of the route's sort options, else newest first. An unknown value is never sent,
 *  so a pasted `?sort=junk` reads the default list rather than a 400. */
function parseSort(raw: string | null): OrderHistorySort {
  const known = ORDER_HISTORY_SORTS.find((option) => option === raw);
  return known ?? DEFAULT_ORDER_HISTORY_SORT;
}

/** A real `YYYY-MM-DD` day, else no bound. `2026-02-31` is rejected here exactly as the
 *  route rejects it, so a URL the server would 400 on never gets sent. */
function parseDay(raw: string | null): string | null {
  if (raw === null) return null;
  return isIsoDay(raw) ? raw : null;
}

/**
 * Write the state back into a query string, preserving any parameter this screen does
 * not own (nothing does today, but a future `?ref=` campaign parameter should survive a
 * page step rather than being silently dropped).
 */
export function writeOrderHistoryUrlState(
  current: URLSearchParams,
  state: OrderHistoryUrlState,
  defaultPageSize: number,
): URLSearchParams {
  const next = new URLSearchParams(current);
  setOrDelete(next, ORDER_HISTORY_PARAMS.page, state.page > 1 ? String(state.page) : null);
  setOrDelete(
    next,
    ORDER_HISTORY_PARAMS.pageSize,
    state.pageSize === defaultPageSize ? null : String(state.pageSize),
  );
  setOrDelete(
    next,
    ORDER_HISTORY_PARAMS.sort,
    state.sort === DEFAULT_ORDER_HISTORY_SORT ? null : state.sort,
  );
  setOrDelete(next, ORDER_HISTORY_PARAMS.submittedFrom, emptyToNull(state.filters.submittedFrom));
  setOrDelete(next, ORDER_HISTORY_PARAMS.submittedTo, emptyToNull(state.filters.submittedTo));
  setOrDelete(next, ORDER_HISTORY_PARAMS.orderRef, emptyToNull(state.filters.orderRef.trim()));
  setOrDelete(next, ORDER_HISTORY_PARAMS.salesRep, state.filters.salesRepOnly ? 'true' : null);
  return next;
}

function setOrDelete(params: URLSearchParams, key: string, value: string | null): void {
  if (value === null) params.delete(key);
  else params.set(key, value);
}

function emptyToNull(value: string | null): string | null {
  return value === null || value === '' ? null : value;
}

/** True when the two filter sets describe the same narrowing. Used to decide whether a
 *  change is worth a history entry (AC8) — re-selecting the day already chosen should
 *  not add a step for browser back to walk through. */
export function sameFilters(left: OrderFilterValues, right: OrderFilterValues): boolean {
  return (
    left.submittedFrom === right.submittedFrom &&
    left.submittedTo === right.submittedTo &&
    left.orderRef.trim() === right.orderRef.trim() &&
    left.salesRepOnly === right.salesRepOnly
  );
}

/** Re-exported so the screen has one import for its filter vocabulary. */
export { NO_ORDER_FILTERS };
